import React, { useState } from "react";
import CloseBtn from "../CloseBtn";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  InputGroup,
} from "react-bootstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const EditUserModal = ({
  show,
  onHide,
  isEdit,
  onSubmit,
  control,
  errors,
  roleOptions,
  rolesLoading,
}) => {
  // 👁️ state for toggling password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header>
        <Modal.Title>{isEdit ? "Edit User" : "Add New User"}</Modal.Title>
      </Modal.Header>
      <CloseBtn onClick={onHide} color="#bf352b" />
      <Form onSubmit={onSubmit} autoComplete="off">
        <Modal.Body>
          {/* Name */}
          <Controller
            name="name"
            control={control}
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Name*</Form.Label>
                <Form.Control {...field} isInvalid={!!errors.name} />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          />

          {/* Email */}
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            }}
            render={({ field }) => (
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email*</Form.Label>
                <Form.Control
                  {...field}
                  autoComplete="off"
                  inputMode="email"
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          />

          {/* Mobile Number */}
          <Controller
            name="mobileNumber"
            control={control}
            rules={{
              required: "Mobile number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Mobile number must be exactly 10 digits",
              },
            }}
            render={({ field }) => (
              <Form.Group className="mb-3" controlId="mobile_number">
                <Form.Label>Mobile Number*</Form.Label>
                <Form.Control
                  {...field}
                  inputMode="numeric"
                  maxLength={10}
                  isInvalid={!!errors.mobileNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobileNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          />

          {/* Roles */}
          <Controller
            name="roles"
            control={control}
            rules={{ required: "Please select at least one role" }}
            render={({ field }) => {
              const options = roleOptions.map((r) => ({
                value: String(r.id),
                label: r.name,
              }));
              const value = (field.value || [])
                .map((id) => options.find((o) => o.value === String(id)))
                .filter(Boolean);
              return (
                <Form.Group className="mb-3" controlId="roles">
                  <Form.Label>Roles*</Form.Label>
                  <Select
                    isMulti
                    isLoading={rolesLoading}
                    options={options}
                    value={value}
                    onChange={(items) =>
                      field.onChange((items || []).map((i) => i.value))
                    }
                    classNamePrefix="react-select"
                    placeholder="Select roles..."
                  />
                  {errors.roles && (
                    <div className="invalid-feedback d-block">
                      {errors.roles.message}
                    </div>
                  )}
                </Form.Group>
              );
            }}
          />

          {/* Password Field (Add User) */}
          {!isEdit && (
            <Controller
              name="password"
              control={control}
              rules={{ required: "Password is required" }}
              render={({ field }) => (
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Password*</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      {...field}
                      isInvalid={!!errors.password}
                      autoComplete="new-password"
                    />
                    <Button
                      // variant="outline-secondary"
                      onClick={() => setShowPassword((prev) => !prev)}
                      type="button"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.password?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              )}
            />
          )}

          {/* Update Password Field (Edit User) */}
          {isEdit && (
            <Controller
              name="updatePassword"
              control={control}
              rules={{
                validate: (value) =>
                  !value ||
                  value.length >= 6 ||
                  "Password must be at least 6 characters",
              }}
              render={({ field }) => (
                <Form.Group className="mb-3" controlId="update_password">
                  <Form.Label>Update Password (optional)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showUpdatePassword ? "text" : "password"}
                      placeholder="Leave blank to keep existing password"
                      {...field}
                      isInvalid={!!errors.updatePassword}
                      autoComplete="new-password"
                    />
                    <Button
                      // variant="outline-secondary"
                      onClick={() => setShowUpdatePassword((prev) => !prev)}
                      type="button"
                    >
                      {showUpdatePassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.updatePassword?.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              )}
            />
          )}

          {/* Status */}
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Form.Group className="mb-3" controlId="status">
                <Form.Label>Status*</Form.Label>
                <Form.Select {...field}>
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </Form.Select>
              </Form.Group>
            )}
          />

          {/* Info */}
          {!isEdit && (
            <Alert variant="info">
              Please ensure your Excel file has columns: Name, Email, Password,
              Mobile Number
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {isEdit ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
